import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ResourceProfileSummary from 'components/ResourceProfileSummary/ResourceProfileSummary'
import { StatMetricTile, StatMetricsGrid, StatMetricsSection } from 'components/StatMetricTile/StatMetricTile'
import TablePagination from 'components/TablePagination/TablePagination'
import { FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS } from 'config/featureFlags'
import { ReactComponent as CalculateRouteSvg } from 'images/icons/calculate_route.svg'
import { ReactComponent as SuccessSvg } from 'images/icons/success.svg'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import {
  selectHighlightedFeatureIdInList,
  selectMissionCalculatingStatus,
  selectMissionFinishPoint,
  selectMissionInfo,
  selectMissionPointRoutes,
  selectMissionReadyForCalculation,
  selectMissionStartPoint,
  selectTransferRoutes,
} from 'store/selectors/mission'
import { MISSION_CALCULATING_STATUSES, TRANSFER_TYPE } from 'utils/constants'
import { isStartPointInsideRestrictZone } from 'utils/intersectionUtils'
import { isSweeperRobotType } from 'utils/servicePointsCatalog'
import { ICleaningZone, IPoint, IRestrictZone } from 'utils/interfaces'

import MissionPointTransferItem from './TransferItems/MissionPointTransferItem'
import StartFinishPointItem from './TransferItems/StartFinishPointItem'
import TransferRouteItem from './TransferItems/TransferRouteItem'
import CleaningZoneItem from './ZoneItem/CleaningZoneItem'
import RestrictedZoneItem from './ZoneItem/RestrictedZoneItem'

import style from './ViewZonesMode.module.css'
import statStyles from 'components/StatMetricTile/StatMetrics.module.css'

type ViewZoneTableItem =
  | { id: string; kind: 'start-point'; point: IPoint }
  | { id: string; kind: 'mission-transfer'; transferType: TRANSFER_TYPE.START | TRANSFER_TYPE.FINISH }
  | { id: string; kind: 'middle-transfer'; zoneId: number }
  | { id: string; kind: 'cleaning-zone'; zone: ICleaningZone; number: number }
  | { id: string; kind: 'finish-point'; point: IPoint }
  | { id: string; kind: 'restrict-zone'; zone: IRestrictZone; number: number }

const buildViewZoneTableItems = ({
  cleaningZones,
  restrictZones,
  missionStartPoint,
  missionFinishPoint,
}: {
  cleaningZones: ICleaningZone[]
  restrictZones: IRestrictZone[]
  missionStartPoint?: IPoint | null
  missionFinishPoint?: IPoint | null
}): ViewZoneTableItem[] => {
  const items: ViewZoneTableItem[] = []

  if (missionStartPoint) {
    items.push({ id: `start-${missionStartPoint.id}`, kind: 'start-point', point: missionStartPoint })
  }

  if (cleaningZones.length > 0 || (missionStartPoint && missionFinishPoint)) {
    items.push({ id: 'transfer-start', kind: 'mission-transfer', transferType: TRANSFER_TYPE.START })
  }

  cleaningZones.forEach((zone, index) => {
    const zoneId = zone.properties.zone_id
    if (index > 0) {
      items.push({ id: `transfer-mid-${zoneId}`, kind: 'middle-transfer', zoneId })
    }
    items.push({ id: `zone-${zoneId}`, kind: 'cleaning-zone', zone, number: index + 1 })
  })

  if (cleaningZones.length > 0) {
    items.push({ id: 'transfer-finish', kind: 'mission-transfer', transferType: TRANSFER_TYPE.FINISH })
  }

  if (missionFinishPoint) {
    items.push({ id: `finish-${missionFinishPoint.id}`, kind: 'finish-point', point: missionFinishPoint })
  }

  restrictZones.forEach((zone, index) => {
    const zoneKey = zone.properties?.zone_id ?? index
    items.push({
      id: `restrict-${zoneKey}`,
      kind: 'restrict-zone',
      zone,
      number: cleaningZones.length + index + 1,
    })
  })

  return items
}

const idsMatch = (a: string | number | undefined, b: string | number | undefined): boolean =>
  a != null && b != null && String(a) === String(b)

const findTableItemIndexForHighlight = (
  items: ViewZoneTableItem[],
  highlightId: string | number | undefined,
  transferRoutes: ReturnType<typeof selectTransferRoutes>,
  missionPointRoutes: ReturnType<typeof selectMissionPointRoutes>
): number => {
  if (highlightId == null) {
    return -1
  }

  return items.findIndex(item => {
    switch (item.kind) {
      case 'start-point':
        return idsMatch(item.point.id, highlightId) || idsMatch(item.point.properties?.id_on_map, highlightId)
      case 'finish-point':
        return idsMatch(item.point.id, highlightId) || idsMatch(item.point.properties?.id_on_map, highlightId)
      case 'cleaning-zone': {
        const zone = item.zone
        return (
          idsMatch(zone.properties?.zone_id, highlightId) ||
          idsMatch(zone.id, highlightId) ||
          idsMatch(zone.properties?.id_on_map, highlightId)
        )
      }
      case 'restrict-zone': {
        const zone = item.zone
        return (
          idsMatch(zone.properties?.zone_id, highlightId) ||
          idsMatch(zone.id, highlightId) ||
          idsMatch(zone.properties?.id_on_map, highlightId)
        )
      }
      case 'middle-transfer': {
        const route = transferRoutes.find(r => r.properties.zone_id_to === item.zoneId)
        return (
          route != null &&
          (idsMatch(route.id, highlightId) ||
            idsMatch(route.properties?.id_on_map, highlightId) ||
            idsMatch(route.properties?.id, highlightId))
        )
      }
      case 'mission-transfer': {
        const route = missionPointRoutes.find(r => r.properties.type === item.transferType)
        return route != null && idsMatch(route.id, highlightId)
      }
      default:
        return false
    }
  })
}

type ViewZonesModeProps = {
  cleaningZones: Array<ICleaningZone>
  restrictZones: Array<IRestrictZone>
  setCalculationMode?: (mode: 'all' | 'single' | undefined) => void
  hideActions?: boolean
  hideCleaningType?: boolean
}

const formatTime = (seconds: number) => {
  if (seconds > 0 && seconds < 60) {
    return '1m'
  }
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) {
    return `${h}h:${m}m`
  }
  return `${m}m`
}

const getCssRowHeight = (): number => {
  const styles = getComputedStyle(document.documentElement)
  const rowHeightVar = styles.getPropertyValue('--row-height').trim()
  const parsed = Number.parseFloat(rowHeightVar)
  if (!Number.isNaN(parsed) && parsed > 0) {
    return parsed
  }
  return 52
}

const visibleRowCount = (containerHeight: number, rowHeight: number): number => {
  if (containerHeight <= 0 || rowHeight <= 0) {
    return 1
  }
  return Math.max(1, Math.floor(containerHeight / rowHeight))
}

const ViewZonesMode = (props: ViewZonesModeProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const missionStartPoint = useSelector(selectMissionStartPoint)
  const missionFinishPoint = useSelector(selectMissionFinishPoint)
  const missionInfo = useSelector(selectMissionInfo)
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)
  const transferRoutes = useSelector(selectTransferRoutes)
  const missionPointRoutes = useSelector(selectMissionPointRoutes)
  const calculatingProgress = useSelector(selectMissionCalculatingStatus)
  const readyForCalculation = useSelector(selectMissionReadyForCalculation)
  const startInsideRestrictZone = useMemo(
    () => isStartPointInsideRestrictZone(missionStartPoint, props.restrictZones),
    [missionStartPoint, props.restrictZones]
  )
  const missionReady = calculatingProgress === MISSION_CALCULATING_STATUSES.OK && !startInsideRestrictZone

  const [currentPage, setCurrentPage] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [pageSize, setPageSize] = useState(20)
  const pageSizeRef = useRef(pageSize)
  pageSizeRef.current = pageSize
  const zoneListRef = useRef<HTMLDivElement>(null)

  const tableItems = useMemo(
    () =>
      buildViewZoneTableItems({
        cleaningZones: props.cleaningZones,
        restrictZones: props.restrictZones,
        missionStartPoint,
        missionFinishPoint,
      }),
    [missionFinishPoint, missionStartPoint, props.cleaningZones, props.restrictZones]
  )

  const totalPages = Math.max(1, Math.ceil(tableItems.length / pageSize))
  const showPagination = tableItems.length > pageSize
  const pageStart = (currentPage - 1) * pageSize
  const pagedItems = tableItems.slice(pageStart, pageStart + pageSize)

  const recalculatePageSize = useCallback(() => {
    const listEl = zoneListRef.current
    if (!listEl || tableItems.length === 0) {
      return
    }
    if (listEl.clientHeight <= 0) {
      return
    }

    const rowHeight = getCssRowHeight()
    const nextPageSize = visibleRowCount(listEl.clientHeight, rowHeight)
    if (nextPageSize !== pageSizeRef.current) {
      setPageSize(nextPageSize)
    }
  }, [tableItems.length])

  useLayoutEffect(() => {
    recalculatePageSize()
  }, [recalculatePageSize])

  useEffect(() => {
    const listEl = zoneListRef.current
    if (!listEl) {
      return
    }

    const observer = new ResizeObserver(recalculatePageSize)
    observer.observe(listEl)
    window.addEventListener('resize', recalculatePageSize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculatePageSize)
    }
  }, [recalculatePageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [missionInfo?.id])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    setPageInput(String(currentPage))
  }, [currentPage])

  useEffect(() => {
    if (highlightedFeatureIdInList == null || tableItems.length === 0 || pageSize <= 0) {
      return
    }
    const itemIndex = findTableItemIndexForHighlight(
      tableItems,
      highlightedFeatureIdInList,
      transferRoutes,
      missionPointRoutes
    )
    if (itemIndex < 0) {
      return
    }
    const page = Math.floor(itemIndex / pageSize) + 1
    setCurrentPage(prev => (prev === page ? prev : page))
    setPageInput(String(page))
  }, [highlightedFeatureIdInList, tableItems, pageSize, transferRoutes, missionPointRoutes])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput, 10)
      if (!Number.isNaN(val) && val >= 1 && val <= totalPages) {
        setCurrentPage(val)
      } else {
        setPageInput(String(currentPage))
      }
    }
  }

  const onCalculateRouteClicked = async () => {
    if (!readyForCalculation || calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING) {
      return
    }
    try {
      await dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.CALCULATING))
      await MainApi.startCalculatingRoutes(missionInfo?.id || -1)
      if (props.setCalculationMode) {
        props.setCalculationMode('all')
      }
    } catch (error) {
      await dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.ERROR))
    }
  }

  const calculationStatusClass = useMemo(() => {
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.ERROR) return style.error
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING) return style.calculating
    if (startInsideRestrictZone) return style.warn
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.OK) return style.ok
    if (readyForCalculation) return style.warn
    return ''
  }, [calculatingProgress, readyForCalculation, startInsideRestrictZone])

  const renderTableItem = (row: ViewZoneTableItem) => {
    switch (row.kind) {
      case 'start-point':
        return (
          <StartFinishPointItem type={TRANSFER_TYPE.START} missionPoint={row.point} hideActions={props.hideActions} />
        )
      case 'finish-point':
        return (
          <StartFinishPointItem type={TRANSFER_TYPE.FINISH} missionPoint={row.point} hideActions={props.hideActions} />
        )
      case 'mission-transfer':
        return <MissionPointTransferItem type={row.transferType} hideActions={props.hideActions} />
      case 'middle-transfer':
        return (
          <TransferRouteItem
            type={TRANSFER_TYPE.MIDDLE}
            zoneIdToTransfer={row.zoneId}
            hideActions={props.hideActions}
          />
        )
      case 'cleaning-zone':
        return (
          <CleaningZoneItem
            zone={row.zone}
            number={row.number}
            hideActions={props.hideActions}
            setCalculationMode={props.setCalculationMode || (() => {})}
          />
        )
      case 'restrict-zone':
        return <RestrictedZoneItem zone={row.zone} number={row.number} hideActions={props.hideActions} />
      default:
        return null
    }
  }

  const hasZoneTable =
    props.cleaningZones.length || props.restrictZones.length || missionStartPoint || missionFinishPoint

  return (
    <div className={style.tableWrapper}>
      <StatMetricsSection>
        <StatMetricsGrid columns="1fr 1fr 1fr 1.7fr">
          <StatMetricTile label="Coverage">
            <>
              {Math.round(missionInfo?.overall_cleaned_area || 0)}m<sup>2</sup>
            </>
          </StatMetricTile>
          <StatMetricTile label="Length">{Math.round(missionInfo?.overall_length || 0)}m</StatMetricTile>
          <StatMetricTile label="Exp Time">{formatTime(missionInfo?.overall_execution_time || 0)}</StatMetricTile>
          <div
            className={clsx(
              statStyles.statCard,
              style.statCardRow,
              readyForCalculation ? style.calcCardInteractive : style.calcCardDisabled
            )}
            onClick={onCalculateRouteClicked}
          >
            <div className={clsx(style.statIconWrapper, style.status, calculationStatusClass)}>
              {missionReady ? (
                <SuccessSvg className={style.statIcon} />
              ) : (
                <CalculateRouteSvg className={style.statIcon} />
              )}
            </div>
            <div className={style.statContent}>
              <div className={statStyles.statLabel}>{missionReady ? 'Status' : 'Press for'}</div>
              <div className={statStyles.statValue} style={{ fontSize: '16px' }}>
                {missionReady
                  ? 'Ready'
                  : calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING
                  ? 'Calculation'
                  : 'Calculation'}
              </div>
            </div>
          </div>
        </StatMetricsGrid>
        {FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS && (
          <ResourceProfileSummary
            profile={missionInfo?.resource_profile}
            hideDirtyWater={isSweeperRobotType(missionInfo?.robot_type)}
          />
        )}
      </StatMetricsSection>
      {startInsideRestrictZone ? (
        <div className={style.startPointRestrictWarning} role="status">
          {t('mission.start_point_in_restrict_zone')}
        </div>
      ) : null}
      {hasZoneTable ? (
        <div className={style.tableListArea}>
          <div
            className={clsx(style.tableListChrome, props.hideCleaningType ? style.tableHeaderShort : style.tableHeader)}
          >
            <div></div>
            <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>{t('view_zones.zone_number')}</div>
            <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>{t('view_zones.type')}</div>
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('view_zones.name')}</div>
            {!props.hideCleaningType && (
              <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('view_zones.cleaning_type')}</div>
            )}
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>
              {props.hideCleaningType ? 'Length/Coverage' : 'Length/ Coverage'}
            </div>
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>Exp time</div>
            <div></div>
          </div>
          <div className={style.zoneListScroll} ref={zoneListRef}>
            {pagedItems.map(row => (
              <div key={row.id} data-view-zone-table-row>
                {renderTableItem(row)}
              </div>
            ))}
          </div>
          {showPagination && (
            <div className={style.paginationChrome}>
              <TablePagination
                page={currentPage}
                totalPages={totalPages}
                pageInput={pageInput}
                onPageInputChange={setPageInput}
                onPageInputKeyDown={handlePageJump}
                onPageInputBlur={() => setPageInput(String(currentPage))}
                onPrev={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                onNext={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={style.emptyWrapper} />
      )}
    </div>
  )
}

export default ViewZonesMode
